/* eslint-disable react/jsx-pascal-case */
import { rowsPerPageOptions } from '../../constants/rowPerPage';
import Box from '@mui/material/Box';
import TextWrapper from './useTextWrapper';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import ButtonWrapper from './useButton';
import { MenuItem, Paper, Switch, TableContainer } from '@mui/material';
import MenuPopover from '../layout/dashboardLayout/shared/components/menuPopover';
import { useEffect, useRef, useState } from 'react';
import { UtilitiesService } from '../../services/utilitiesService';
import DownloadIcon from '@mui/icons-material/Download';
import MaterialReactTable from 'material-react-table';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PropTypes from 'prop-types';

DataTableGrid.propTypes = {
	name: PropTypes.string,
	columns: PropTypes.array,
	records: PropTypes.any,
	isPagination: PropTypes.bool,
	pagination: PropTypes.any,
	loading: PropTypes.bool,
	heading: PropTypes.string,
	showEditColumnsButton: PropTypes.bool,
	showDownloadCSV: PropTypes.bool,
	rowHeight: PropTypes.number,
	actionButtons: PropTypes.element,
	columnPinning: PropTypes.any,
	isEditTable: PropTypes.bool,
	hideSerialNumbers: PropTypes.bool,
	onRowClick: PropTypes.func,
	onPageChange: PropTypes.func,
	onPageSizeChange: PropTypes.func,
	download: PropTypes.func,
	onCellClick: PropTypes.func,
	handleSaveCell: PropTypes.func,
};

const serialNumberHeader = {
	header: 'Sl.No ',
	accessorKey: 'serialNumber',
	enableEditing: false,
	maxSize: 100,
};

const utilitiesService = new UtilitiesService();

export default function DataTableGrid({
	name,
	columns = [],
	records = [],
	isPagination = true,
	pagination,
	loading = false,
	heading = null,
	showEditColumnsButton = true,
	showDownloadCSV = false,
	rowHeight = 'comfortable',
	actionButtons = null,
	columnPinning = [],
	columnPinningLeft = [],
	isEditTable = false,
	hideSerialNumbers = false,
	enableExpanding = true,
	enableRowDragging = false,
	enableRowOrdering = false,
	isShowDetailTab = false,
	DetailTabContent = <></>,
	onRowClick = (e) => { },
	onPageChange = (e) => { },
	onPageSizeChange = (e) => { },
	download = (e) => { },
	onCellClick = (e) => { },
	handleSaveCell = (cell, value) => { },
	onRowOrderChange = (hoveredRow, draggingRow) => { },
}) {
	const tableInstanceRef = useRef(null);
	const [visibilityModel, setVisibilityModel] = useState({});
	const [open, setOpen] = useState(null);

	const handleOpen = (event) => {
		setOpen(event.currentTarget);
	};

	const handleClose = () => {
		setOpen(null);
	};

	const getAllVisiblityModel = (tblName, columns = []) => {
		let visibilityModel = {};

		let storedVisibilityModel =
			JSON?.parse(utilitiesService.getLocalStorage({ item: tblName })) || null;

		if (storedVisibilityModel) {
			return storedVisibilityModel;
		}

		columns.forEach((res) => {
			Object.assign(visibilityModel, { [res.accessorKey]: !res?.hide });
		});
		return visibilityModel;
	};

	const onColumnChange = (col) => {
		let oldVisibilityModel = getAllVisiblityModel(name, columns);
		oldVisibilityModel[col.accessorKey] = !col.hide;
		utilitiesService.setLocalStorage({
			item: name,
			value: JSON.stringify(oldVisibilityModel),
		});
		setVisibilityModel(getAllVisiblityModel(name, columns));
	};

	const getActualColumns = (tblName, columns = []) => {
		let visibilityModel = getAllVisiblityModel(tblName, columns);
		columns.forEach((col) => {
			col.hide = !visibilityModel[col.accessorKey];
			col.enableEditing = col?.enableEditing ? true : false;
		});
		if (!hideSerialNumbers) {
			columns = [serialNumberHeader, ...columns];
		}
		return columns;
	};

	const getChildrenRows = () => {
		records.forEach((resp, index) => {
			if (!hideSerialNumbers) {
				resp[serialNumberHeader.accessorKey] =
					((pagination?.currentPage || 1) - 1) * (pagination?.perPage || 0) +
					index +
					1;
			}
		});
		return records;
	};

	useEffect(() => {
		if (showEditColumnsButton) {
			setVisibilityModel(getAllVisiblityModel(name, columns));
		}
	}, [columns, name, showEditColumnsButton]);

	if (showEditColumnsButton) {
		columns = getActualColumns(name, columns);
	}

	records = getChildrenRows();

	useEffect(() => {
		tableInstanceRef?.current.setColumnVisibility(visibilityModel);
	}, [visibilityModel]);

	return (
		<TableContainer
			component={Paper}
			sx={{ width: '100%' }}>
			{(heading || showEditColumnsButton || showDownloadCSV) && (
				<Box className="w-full text-start px-3 pt-5 mb-5 flex flex-col md:flex-row justify-between items-center">
					<TextWrapper
						classList="!mb-2 md:!mb-0 w-full md:w-auto"
						type="h6"
						text={heading}></TextWrapper>
					<Box className="grid grid-cols-1 md:grid-flow-col gap-2">
						{showEditColumnsButton ? (
							<>
								<ButtonWrapper
									disabled={loading}
									classList="md:w-[140px] w-full"
									onClick={handleOpen}
									icon={<VerticalSplitIcon />}
									label={'Fields'}
									variant="outlined"></ButtonWrapper>
							</>
						) : null}
						{showDownloadCSV ? (
							<ButtonWrapper
								disabled={loading || !records?.length}
								classList="md:w-[140px] w-full"
								onClick={download}
								icon={<DownloadIcon />}
								label={'Download'}></ButtonWrapper>
						) : null}
						{actionButtons ? actionButtons : null}
					</Box>

					<MenuPopover
						open={Boolean(open)}
						anchorEl={open}
						onClose={handleClose}
						sx={{
							p: 0,
							mt: 1.5,
							ml: 0.75,
							minWidth: '300px',
							maxHeight: '400px',
							overflow: 'auto',
							'& .MuiMenuItem-root': {
								typography: 'body2',
								borderRadius: 0.75,
							},
						}}>
						{columns.map((col, index) => {
							return (
								<MenuItem
									key={col.accessorKey}
									disabled={col.accessorKey === 'action'}>
									<Box className="flex justify-between items-center w-full">
										<Box className="caption">{col.header}</Box>
										<Switch
											checked={!col.hide}
											onChange={(e) => {
												col.hide = !col.hide;
												onColumnChange(col);
											}}
											size="small"></Switch>
									</Box>
								</MenuItem>
							);
						})}
					</MenuPopover>
				</Box>
			)}
			<Box className={'table-wrapper'}>
				<MaterialReactTable
					renderDetailPanel={
						!isShowDetailTab
							? null
							: (row) => {
								return <DetailTabContent row={row}></DetailTabContent>;
							}
					}
					autoResetPageIndex={false}
					columns={columns}
					data={records}
					enableRowOrdering={enableRowOrdering}
					enableSorting={false}
					muiTableBodyRowDragHandleProps={({ table }) => ({
						onDragEnd: () => {
							const { draggingRow, hoveredRow } = table.getState();
							if (hoveredRow && draggingRow) {
								onRowOrderChange && onRowOrderChange(hoveredRow, draggingRow);
							}
						},
					})}
					paginateExpandedRows={true}
					getSubRows={(originalRow) => originalRow?.children}
					enableEditing={isEditTable}
					enableExpanding={enableExpanding}
					enableRowDragging={enableRowDragging}
					editingMode="table"
					tableInstanceRef={tableInstanceRef}
					enableColumnOrdering={false}
					enableFullScreenToggle={false}
					enablePagination={isPagination && records?.length ? true : false}
					manualPagination={isPagination && records?.length ? true : false}
					enableColumnActions={false}
					enableTopToolbar={false}
					enablePinning={true}
					muiTablePaperProps={{ elevation: 0 }}
					muiTableBodyProps={{
						sx: {
							'& tr:nth-of-type(odd)': {
								backgroundColor: '#f5f5f5',
							},
							'& tr:hover': {
								'& td': {
									backgroundColor: 'white',
								},
							},
						},
					}}
					muiTableBodyCellEditTextFieldProps={({ cell }) => ({
						onChange: (event) => {
							handleSaveCell(cell, event.target.value);
						},
						variant: 'outlined',
						className: 'inline-edit-field',
					})}
					muiTableBodyCellProps={({ cell }) => ({
						className: 'hover:!outline-0',
						onClick: (event) => {
							const values = {
								row: cell.row?.original,
								field: cell.column.id,
								cell: cell,
							};

							onCellClick && onCellClick(values);
						},
					})}
					initialState={{
						columnPinning: { right: columnPinning, left: columnPinningLeft },
						density: 'comfortable',
					}}
					enableDensityToggle={false}
					enableFilters={false}
					manualFiltering={true}
					muiTablePaginationProps={{
						onRowsPerPageChange: (e) => {
							onPageSizeChange && onPageSizeChange(e.target.value);
						},
						onPageChange: (e, page) => {
							onPageChange(page);
						},
						rowsPerPageOptions: rowsPerPageOptions,
						showFirstButton: true,
						showLastButton: true,
					}}
					pageCount={
						isPagination && records.length
							? pagination?.totalCount
							: records?.length
					}
					state={{
						density: rowHeight === 'auto' ? 'comfortable' : 'compact',
						showSkeletons: loading,
						pagination: {
							pageIndex: pagination?.currentPage - 1 || 0,
							pageSize:
								isPagination && records.length
									? pagination?.perPage
									: records.length,
						},
					}}
					rowCount={
						isPagination && records.length
							? pagination?.totalCount
							: records?.length
					}
					muiTableBodyCellCopyButtonProps={({ cell }) => ({
						onClick: () => {
							// temporary fix, need to find better solution for copy the content from objects.
							if (cell.column.id === 'contactNumber') {
								let code = cell.row.original?.contactNumber?.code?.replace(/\+/g, "");
								let phoneNumber = `00${code} ${cell.row.original.contactNumber.number}`;
								navigator.clipboard.writeText(phoneNumber);
							} else {
								navigator.clipboard.writeText(
									`${cell.row.original[cell.column.id]}`
								);
							}
						},
						startIcon: cell.row.original[cell.column.id] ? (
							<ContentCopyIcon />
						) : null,
					})}
				/>
			</Box>
		</TableContainer>
	);
}
